import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { Attendee } from "../../types/attendee";
import { AttendeeDetails } from "./AttendeeDetails";
import { CARD_FILTER_OPTIONS, CardFilter, activeCard, cardTypeLabel, formatCard } from "./cardOnFile";
import { ATTENDEE_TYPE_FILTER_OPTIONS, AttendeeTypeFilter } from "./attendeeType";

export type SortField = "id" | "name" | "event" | "validation_code" | "email" | "phone" | "created_at";
export type SortDir = "asc" | "desc";

interface Props {
  attendees: Attendee[];
  query: string;
  onQueryChange: (q: string) => void;
  editPermission: boolean;
  cardFilter: CardFilter;
  onCardFilterChange: (f: CardFilter) => void;
  typeFilter: AttendeeTypeFilter;
  onTypeFilterChange: (f: AttendeeTypeFilter) => void;
  /** Count over the base filters (search/date/event/type), ignoring the card toggle. */
  allCount: number;
  /** Subset of allCount with an active card on file. */
  withCardCount: number;
  onBulkSetInactive?: (ids: number[]) => Promise<void>;
  bulkPending?: boolean;
  sortField: SortField;
  sortDir: SortDir;
  onSortChange: (field: SortField) => void;
  page: number;
  total: number;
  totalPages: number;
  isLoading: boolean;
  onPrev: () => void;
  onNext: () => void;
}

const columns: { field: SortField | null; label: string; numeric?: boolean; hideOn?: string }[] = [
  { field: "id", label: "ID", numeric: true },
  { field: "name", label: "Name" },
  { field: "event", label: "Event", hideOn: "md" },
  { field: "validation_code", label: "Validation Code", hideOn: "md" },
  { field: "phone", label: "Phone", hideOn: "lg" },
  { field: "email", label: "Email", hideOn: "sm" },
  { field: null, label: "Card", hideOn: "md" },
  { field: "created_at", label: "Created", hideOn: "lg" },
  { field: null, label: "" },
];

const hideClass = (hideOn?: string) => {
  switch (hideOn) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    default:
      return "";
  }
};

const fullName = (a: Attendee) => `${a.first_name ?? ""} ${a.last_name ?? ""}`.trim();

export const AttendeesTable = ({
  attendees,
  query,
  onQueryChange,
  editPermission,
  cardFilter,
  onCardFilterChange,
  typeFilter,
  onTypeFilterChange,
  allCount,
  withCardCount,
  onBulkSetInactive,
  bulkPending,
  sortField,
  sortDir,
  onSortChange,
  page,
  total,
  totalPages,
  isLoading,
  onPrev,
  onNext,
}: Props) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const clearSelection = () => setSelected(new Set());

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const visibleSelectedCount = attendees.reduce((n, a) => n + (selected.has(a.id) ? 1 : 0), 0);
  const allVisibleSelected = attendees.length > 0 && visibleSelectedCount === attendees.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        attendees.forEach((a) => n.delete(a.id));
      } else {
        attendees.forEach((a) => n.add(a.id));
      }
      return n;
    });
  };

  const handleSetInactive = async () => {
    if (!onBulkSetInactive || selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Set ${count} attendee${count === 1 ? "" : "s"} to Inactive?`)) return;
    await onBulkSetInactive([...selected]);
    clearSelection();
  };

  const withCardPct = allCount > 0 ? Math.round((withCardCount / allCount) * 100) : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800">Attendees</div>
          <div className="text-xs text-gray-500 tabular-nums">
            <span className="font-semibold text-gray-700">{withCardCount.toLocaleString()}</span>
            {" of "}
            <span className="font-semibold text-gray-700">{allCount.toLocaleString()}</span>
            {` have a card on file${allCount > 0 ? ` (${withCardPct}%)` : ""}`}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <FilterToggle
            label="Filter by attendee type"
            options={ATTENDEE_TYPE_FILTER_OPTIONS}
            value={typeFilter}
            onChange={onTypeFilterChange}
          />
          <FilterToggle
            label="Filter by card on file"
            options={CARD_FILTER_OPTIONS}
            value={cardFilter}
            onChange={onCardFilterChange}
          />
          <div className="relative w-48 sm:w-64">
            <input
              type="text"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search attendees…"
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
            />
            <svg
              className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-red-50 border-b border-red-200">
          <div className="text-sm text-red-900">
            <span className="font-semibold">{selected.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              disabled={bulkPending}
              className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Clear
            </button>
            {editPermission && onBulkSetInactive && (
              <button
                type="button"
                onClick={handleSetInactive}
                disabled={bulkPending}
                className="px-3 py-1 text-xs font-semibold rounded-md bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bulkPending ? "Working…" : "Set Inactive"}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-x-auto relative">
        {isLoading && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10">
            <div className="text-xs text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm">
              Loading…
            </div>
          </div>
        )}
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-gray-200">
              <th className="w-10 px-3 py-2">
                {editPermission && (
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someVisibleSelected;
                    }}
                    onChange={toggleAllVisible}
                    aria-label="Select all visible"
                    className="h-4 w-4 rounded border-gray-300 text-red-700 focus:ring-red-500 cursor-pointer"
                  />
                )}
              </th>
              <th className="w-8 px-2 py-2" />
              {columns.map((col, idx) => (
                <th
                  key={col.field ?? `col-${idx}`}
                  onClick={() => col.field && onSortChange(col.field)}
                  className={`px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 font-semibold select-none ${
                    col.field ? "cursor-pointer hover:text-gray-800" : ""
                  } ${col.numeric ? "text-right" : "text-left"} ${hideClass(col.hideOn)}`}
                >
                  {col.label && (
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {col.field && <SortIcon active={sortField === col.field} dir={sortDir} />}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {attendees.length === 0 && !isLoading && (
              <tr>
                <td colSpan={columns.length + 2} className="px-4 py-8 text-center text-gray-400 text-sm">
                  {query ? `No attendees match "${query}".` : "No attendees to display."}
                </td>
              </tr>
            )}
            {attendees.map((a) => {
              const isOpen = expanded.has(a.id);
              const isSelected = selected.has(a.id);
              const card = activeCard(a);
              return (
                <Fragment key={a.id}>
                  <tr
                    onClick={() => toggle(a.id)}
                    className={`cursor-pointer transition-colors ${
                      isSelected ? "bg-red-50" : isOpen ? "bg-red-50/40" : "hover:bg-gray-50"
                    }`}
                  >
                    <td
                      className="px-3 py-2.5 align-middle"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {editPermission && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(a.id)}
                          aria-label={`Select attendee ${a.id}`}
                          className="h-4 w-4 rounded border-gray-300 text-red-700 focus:ring-red-500 cursor-pointer"
                        />
                      )}
                    </td>
                    <td className="px-2 py-2.5 align-middle">
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right tabular-nums text-gray-700">{a.id}</td>
                    <td className="px-3 py-2.5 align-middle">
                      <span className="font-medium text-gray-900">{fullName(a) || "(no name)"}</span>
                      <div className="sm:hidden text-xs text-gray-500 truncate">{a.email || a.phone_number}</div>
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("md")}`}>
                      {a.event?.name || "—"}
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-gray-700 tabular-nums ${hideClass("md")}`}>
                      {a.validation_code || "—"}
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("lg")}`}>
                      {a.phone_number || "—"}
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-gray-700 ${hideClass("sm")}`}>
                      {a.email || "—"}
                    </td>
                    <td className={`px-3 py-2.5 align-middle whitespace-nowrap ${hideClass("md")}`}>
                      {card ? (
                        <span className="inline-flex items-center gap-1.5 tabular-nums text-gray-800">
                          <span>{formatCard(card)}</span>
                          {cardTypeLabel(card) && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">{cardTypeLabel(card)}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 align-middle text-gray-700 whitespace-nowrap ${hideClass("lg")}`}>
                      {a.created_at ? moment(a.created_at).format("MM/DD/YYYY") : "—"}
                    </td>
                    <td className="px-3 py-2.5 align-middle text-right">
                      {editPermission && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/attendees/${a.id}/edit`);
                          }}
                          className="px-3 py-1 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 transition-colors"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={columns.length + 2} className="p-0">
                        <AttendeeDetails attendee={a} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-gray-100 bg-gray-50">
        <div className="text-xs text-gray-500">
          {total === 0
            ? "0 results"
            : `Page ${page + 1} of ${totalPages.toLocaleString()}`}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={page === 0 || isLoading}
            className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={page + 1 >= totalPages || isLoading}
            className="px-3 py-1 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterToggle = <T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-xs font-medium"
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(opt.value)}
          className={`px-2.5 py-1 rounded whitespace-nowrap transition-colors ${
            active ? "bg-red-700 text-white" : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => (
  <svg className={`w-3 h-3 ${active ? "text-gray-700" : "text-gray-300"}`} fill="currentColor" viewBox="0 0 20 20">
    {dir === "asc" && active ? (
      <path d="M5 13l5-5 5 5H5z" />
    ) : dir === "desc" && active ? (
      <path d="M5 7l5 5 5-5H5z" />
    ) : (
      <>
        <path d="M5 9l5-5 5 5H5z" />
        <path d="M5 11l5 5 5-5H5z" opacity="0.5" />
      </>
    )}
  </svg>
);
